import * as THREE from 'three';

/**
 * 유체 시뮬레이션 — 원본 `Fluid Sim` 의 이식.
 *
 * <h2>커서가 «민» 자국이 물처럼 퍼진다</h2>
 * 궤적을 그려 두는 것과는 다르다. 커서가 속도장에 힘을 밀어 넣으면 그 뒤로는 물리가
 * 굴러간다 — 소용돌이가 생기고, 압력이 퍼지고, 서서히 잦아든다.
 *
 * <h2>한 프레임에 여덟 번 그린다</h2>
 * splat → curl → vorticity → divergence → clear → pressure(야코비) → gradientSubtract → advect.
 * 셰이더 본문은 원본 그대로고, WebGL2 전용 `texture()` 만 `texture2D()` 로 바꿨다
 * (three 의 ShaderMaterial 이 GLSL 1.00 으로 엮는다. 같은 함수다).
 *
 * <p>결과인 속도장이 곧 `tFluidSim` 이다. 배경 셰이더는 이것의 밝기를, 입자 시뮬은
 * 이것을 <b>변위 벡터</b>로 읽는다.
 */

/** 원본 `Fluid Sim` 실측값. */
export const FLUID = {
  mouseRadius: 0.1,
  pressure: 0.9883000000000002,
  curlStrength: 0.3675000000000004,
  dissipation: 0.034999999999999615,
  clearValue: 0,
  viscosity: 0.484126984126984,
  forceClamp: 20,
  force: 5,
  iterations: 20,
  size: 128,
};

const BASE_VERT = `varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform vec2 uTexelSize;void main(){vUv=uv;vL=vUv-vec2(uTexelSize.x,0.0);vR=vUv+vec2(uTexelSize.x,0.0);vT=vUv+vec2(0.0,uTexelSize.y);vB=vUv-vec2(0.0,uTexelSize.y);gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`;
const PLAIN_VERT = `varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}`;
const SPLAT_FRAG = `varying vec2 vUv;uniform sampler2D tBase;uniform vec2 uTexelSize;uniform vec2 uResolution;uniform float uNormalizedDelta;uniform float uScreenAspect;uniform vec3 uForce;uniform vec2 uMouse;uniform vec2 uPrevMouse;uniform vec2 uMouseVelocity;uniform float uMouseRadius;uniform float uPressure;uniform float uMouseStrength;void main(){vec4 color=texture2D(tBase,vUv);float magnitude=1.0-smoothstep(0.0,uMouseRadius,distance(vUv*vec2(uScreenAspect,1.),uMouse*vec2(uScreenAspect,1.)));vec3 splat=uForce*magnitude*uMouseStrength;color.rgb=(color.rgb+splat)*uPressure;color.a=1.0;gl_FragColor=color;}`;
const CURL_FRAG = `varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform sampler2D tVelocity;void main(){float L=texture2D(tVelocity,vL).y;float R=texture2D(tVelocity,vR).y;float T=texture2D(tVelocity,vT).x;float B=texture2D(tVelocity,vB).x;float vorticity=R-L-T+B;gl_FragColor=vec4(0.5*vorticity,0.0,0.0,1.0);}`;
const VORTICITY_FRAG = `varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform sampler2D tVelocity;uniform sampler2D tCurl;uniform float uCurlStrength;uniform float uNormalizedDelta;void main(){float L=texture2D(tCurl,vL).x;float R=texture2D(tCurl,vR).x;float T=texture2D(tCurl,vT).x;float B=texture2D(tCurl,vB).x;float C=texture2D(tCurl,vUv).x;vec2 force=0.5*vec2(abs(T)-abs(B),abs(R)-abs(L));force/=length(force)+0.01;force*=uCurlStrength*C;force.y*=-1.0;vec2 vel=texture2D(tVelocity,vUv).xy;gl_FragColor=vec4(vel+force*uNormalizedDelta,0.0,1.0);}`;
const DIVERGENCE_FRAG = `varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform sampler2D tVelocity;uniform float uViscosity;void main(){float L=texture2D(tVelocity,vL).x;float R=texture2D(tVelocity,vR).x;float T=texture2D(tVelocity,vT).y;float B=texture2D(tVelocity,vB).y;vec2 C=texture2D(tVelocity,vUv).xy;if(vL.x<0.0){L=-C.x;}if(vR.x>1.0){R=-C.x;}if(vT.y>1.0){T=-C.y;}if(vB.y<0.0){B=-C.y;}float div=0.5*(R-L+T-B)*uViscosity;gl_FragColor=vec4(div,0.0,0.0,1.0);}`;
const CLEAR_FRAG = `varying vec2 vUv;uniform sampler2D tPressure;uniform float uClearValue;void main(){gl_FragColor=uClearValue*texture2D(tPressure,vUv);}`;
const PRESSURE_FRAG = `varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform vec2 uTexelSize;uniform sampler2D tTexture;uniform sampler2D tPressure;uniform sampler2D tDivergence;vec2 boundary(vec2 uv){return uv;}void main(){float L=texture2D(tTexture,boundary(vL)).x;float R=texture2D(tTexture,boundary(vR)).x;float T=texture2D(tTexture,boundary(vT)).x;float B=texture2D(tTexture,boundary(vB)).x;float C=texture2D(tTexture,vUv).x;float divergence=texture2D(tDivergence,vUv).x;float pressure=(L+R+B+T-divergence)*0.25;gl_FragColor=vec4(pressure,0.0,0.0,1.0);}`;
const GRADIENT_FRAG = `varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform vec2 uTexelSize;uniform vec2 uResolution;uniform sampler2D tPressure;uniform sampler2D tVelocity;vec2 boundary(vec2 uv){return uv;}void main(){float L=texture2D(tPressure,boundary(vL)).x;float R=texture2D(tPressure,boundary(vR)).x;float T=texture2D(tPressure,boundary(vT)).x;float B=texture2D(tPressure,boundary(vB)).x;vec2 velocity=texture2D(tVelocity,vUv).xy;velocity.xy-=vec2(R-L,T-B);gl_FragColor=vec4(velocity,0.0,1.0);}`;
const ADVECT_FRAG = `varying vec2 vUv;uniform vec2 uTexelSize;uniform sampler2D tTexture;uniform sampler2D tVelocity;uniform float uNormalizedDelta;uniform float uDelta;uniform float uDissipation;void main(){vec2 coord=vUv-uNormalizedDelta*texture2D(tTexture,vUv).xy*uTexelSize;vec4 result=texture2D(tVelocity,coord);float decay=1.0+uDissipation*uNormalizedDelta;gl_FragColor=result/decay;}`;

const RT = {
  type: THREE.HalfFloatType,
  format: THREE.RGBAFormat,
  minFilter: THREE.LinearFilter,
  magFilter: THREE.LinearFilter,
  depthBuffer: false,
  stencilBuffer: false,
} as const;

function pair(n: number) {
  const st = {
    read: new THREE.WebGLRenderTarget(n, n, RT),
    write: new THREE.WebGLRenderTarget(n, n, RT),
    swap() {
      const t = st.read;
      st.read = st.write;
      st.write = t;
    },
  };
  return st;
}

/**
 * 판 크기와 반복 횟수를 <b>부르는 쪽이 낮출 수 있게</b> 연 자리 (2026-09-21).
 *
 * <p>{@link FLUID} 의 {@code size}·{@code iterations} 는 원본 실측값이고, 웹 히어로는 그대로 쓴다.
 * 앱 구슬처럼 <b>지름 68dp</b> 로 서는 자리는 사정이 다르다 — 이 시뮬의 비용은 화면에 그려지는
 * 크기와 <b>무관</b>해서, 136px 짜리 원 하나가 1,280px 히어로와 똑같은 격자를 돌린다.
 */
export type FluidSimOptions = {
  /** 격자 한 변. 안 주면 {@link FLUID}{@code .size}(128). */
  size?: number;
  /** 압력 반복 횟수. 안 주면 {@link FLUID}{@code .iterations}(20). */
  iterations?: number;
};

export function createFluidSim(renderer: THREE.WebGLRenderer, options: FluidSimOptions = {}) {
  const n = Math.max(16, Math.round(options.size ?? FLUID.size));
  const iterations = Math.max(1, Math.round(options.iterations ?? FLUID.iterations));
  const texel = new THREE.Vector2(1 / n, 1 / n);
  const resolution = new THREE.Vector2(n, n);

  const velocity = pair(n);
  const pressure = pair(n);
  const divergence = new THREE.WebGLRenderTarget(n, n, RT);
  const curl = new THREE.WebGLRenderTarget(n, n, RT);

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const geo = new THREE.PlaneGeometry(2, 2);
  const quad = new THREE.Mesh(geo);
  scene.add(quad);

  const shared = {
    uTexelSize: { value: texel },
    uResolution: { value: resolution },
    uNormalizedDelta: { value: 1 },
  };
  const mat = (v: string, f: string, u: Record<string, THREE.IUniform>) =>
    new THREE.ShaderMaterial({ vertexShader: v, fragmentShader: f, uniforms: u, depthTest: false, depthWrite: false });

  const splat = mat(PLAIN_VERT, SPLAT_FRAG, {
    ...shared,
    tBase: { value: null },
    uScreenAspect: { value: 1 },
    uForce: { value: new THREE.Vector3() },
    uMouse: { value: new THREE.Vector2(0.5, 0.5) },
    uPrevMouse: { value: new THREE.Vector2(0.5, 0.5) },
    uMouseVelocity: { value: new THREE.Vector2() },
    uMouseRadius: { value: FLUID.mouseRadius },
    uPressure: { value: FLUID.pressure },
    uMouseStrength: { value: FLUID.force },
  });
  const curlMat = mat(BASE_VERT, CURL_FRAG, { ...shared, tVelocity: { value: null } });
  const vortMat = mat(BASE_VERT, VORTICITY_FRAG, {
    ...shared,
    tVelocity: { value: null },
    tCurl: { value: curl.texture },
    uCurlStrength: { value: FLUID.curlStrength },
  });
  const divMat = mat(BASE_VERT, DIVERGENCE_FRAG, {
    ...shared,
    tVelocity: { value: null },
    uViscosity: { value: FLUID.viscosity },
  });
  const clearMat = mat(PLAIN_VERT, CLEAR_FRAG, {
    tPressure: { value: null },
    uClearValue: { value: FLUID.clearValue },
  });
  const pressMat = mat(BASE_VERT, PRESSURE_FRAG, {
    ...shared,
    tTexture: { value: null },
    tPressure: { value: null },
    tDivergence: { value: divergence.texture },
  });
  const gradMat = mat(BASE_VERT, GRADIENT_FRAG, {
    ...shared,
    tPressure: { value: null },
    tVelocity: { value: null },
  });
  const advMat = mat(PLAIN_VERT, ADVECT_FRAG, {
    ...shared,
    tTexture: { value: null },
    tVelocity: { value: null },
    uDelta: { value: 16 },
    uDissipation: { value: FLUID.dissipation },
  });

  const blit = (m: THREE.ShaderMaterial, target: THREE.WebGLRenderTarget) => {
    quad.material = m;
    renderer.setRenderTarget(target);
    renderer.render(scene, camera);
    renderer.setRenderTarget(null);
  };

  const mouse = new THREE.Vector2(0.5, 0.5);
  const prev = new THREE.Vector2(0.5, 0.5);
  let moved = false;
  let aspect = 1;

  /** 커서를 0‥1 로 받는다. y 는 아래가 0(GL 좌표). */
  const setPointer = (x: number, y: number, screenAspect: number) => {
    prev.copy(mouse);
    mouse.set(x, y);
    aspect = screenAspect;
    moved = true;
  };

  const step = (deltaMs: number) => {
    shared.uNormalizedDelta.value = Math.min(2, deltaMs / 16.666);

    const vx = THREE.MathUtils.clamp((mouse.x - prev.x) * 100, -FLUID.forceClamp, FLUID.forceClamp);
    const vy = THREE.MathUtils.clamp((mouse.y - prev.y) * 100, -FLUID.forceClamp, FLUID.forceClamp);
    const u = splat.uniforms;
    (u.uForce!.value as THREE.Vector3).set(moved ? vx : 0, moved ? vy : 0, 0);
    (u.uMouse!.value as THREE.Vector2).copy(mouse);
    (u.uPrevMouse!.value as THREE.Vector2).copy(prev);
    (u.uMouseVelocity!.value as THREE.Vector2).set(vx, vy);
    u.uScreenAspect!.value = aspect;
    u.tBase!.value = velocity.read.texture;
    blit(splat, velocity.write);
    velocity.swap();
    prev.copy(mouse);
    moved = false;

    curlMat.uniforms.tVelocity!.value = velocity.read.texture;
    blit(curlMat, curl);

    vortMat.uniforms.tVelocity!.value = velocity.read.texture;
    blit(vortMat, velocity.write);
    velocity.swap();

    divMat.uniforms.tVelocity!.value = velocity.read.texture;
    blit(divMat, divergence);

    clearMat.uniforms.tPressure!.value = pressure.read.texture;
    blit(clearMat, pressure.write);
    pressure.swap();

    for (let i = 0; i < iterations; i++) {
      pressMat.uniforms.tTexture!.value = pressure.read.texture;
      pressMat.uniforms.tPressure!.value = pressure.read.texture;
      blit(pressMat, pressure.write);
      pressure.swap();
    }

    gradMat.uniforms.tPressure!.value = pressure.read.texture;
    gradMat.uniforms.tVelocity!.value = velocity.read.texture;
    blit(gradMat, velocity.write);
    velocity.swap();

    advMat.uniforms.tTexture!.value = velocity.read.texture;
    advMat.uniforms.tVelocity!.value = velocity.read.texture;
    advMat.uniforms.uDelta!.value = deltaMs;
    blit(advMat, velocity.write);
    velocity.swap();
  };

  const dispose = () => {
    geo.dispose();
    [splat, curlMat, vortMat, divMat, clearMat, pressMat, gradMat, advMat].forEach((m) => m.dispose());
    velocity.read.dispose();
    velocity.write.dispose();
    pressure.read.dispose();
    pressure.write.dispose();
    divergence.dispose();
    curl.dispose();
  };

  return {
    step,
    setPointer,
    dispose,
    get texture() {
      return velocity.read.texture;
    },
  };
}
